import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { PageHeader, EmptyState, Modal, Field, Button } from "../components/UI";
import { CheckSquare, Plus, Trash2, Check, Calendar, AlertCircle, Clock } from "lucide-react";
import { toast } from "sonner";

const priorityMeta = {
  high: { cls: "bg-red-100 text-red-800", dot: "bg-red-500", label: "High" },
  medium: { cls: "bg-amber-100 text-amber-800", dot: "bg-amber-500", label: "Med" },
  low: { cls: "bg-slate-100 text-slate-600", dot: "bg-slate-400", label: "Low" },
};

const blank = { title: "", description: "", assigned_to: "", due_date: "", priority: "medium", related_type: "other" };

function todayIso() { return new Date().toISOString().slice(0, 10); }
function plusDaysIso(n) { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); }

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [dateFilter, setDateFilter] = useState("today");
  const [statusFilter, setStatusFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status_filter = statusFilter;
      if (dateFilter === "today") params.date = todayIso();
      if (dateFilter === "tomorrow") params.date = plusDaysIso(1);
      const r = await api.get("/tasks", { params });
      let data = r.data;
      if (dateFilter === "overdue") {
        const today = todayIso();
        data = data.filter((t) => t.due_date && t.due_date < today && t.status === "pending");
      }
      if (dateFilter === "upcoming") {
        const today = todayIso();
        data = data.filter((t) => !t.due_date || t.due_date > today);
      }
      setTasks(data);
      if (user?.role === "admin") {
        try { setUsers((await api.get("/users")).data); } catch (e) { /* ignore */ }
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [dateFilter, statusFilter]);

  const openNew = (preset = {}) => {
    setForm({ ...blank, assigned_to: user?.role === "admin" ? "" : user?.id || "", due_date: todayIso(), ...preset });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.due_date) delete payload.due_date;
      await api.post("/tasks", payload);
      toast.success("Task added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggle = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status: t.status === "done" ? "pending" : "done" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try { await api.delete(`/tasks/${t.id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-4 lg:p-8 max-w-5xl" data-testid="tasks-page">
      <PageHeader
        title="To-Do & Plans"
        subtitle="Plan your day, set reminders, and track work assigned by you or admin"
        action={<Button onClick={() => openNew()} data-testid="add-task-btn"><Plus size={16}/>Add Task</Button>}
      />

      <div className="flex flex-wrap gap-2 mb-5">
        {[
          { v: "today", l: "Today" },
          { v: "tomorrow", l: "Tomorrow" },
          { v: "overdue", l: "Overdue" },
          { v: "upcoming", l: "Upcoming" },
          { v: "all", l: "Any Date" },
        ].map((f) => (
          <button key={f.v} data-testid={`task-date-${f.v}`} onClick={() => setDateFilter(f.v)}
            className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold transition ${dateFilter === f.v ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569] hover:border-[#C1121F]"}`}>
            {f.l}
          </button>
        ))}
        <div className="w-px bg-[#E5E7EB] mx-1" />
        {[
          { v: "pending", l: "Pending" },
          { v: "done", l: "Done" },
          { v: "all", l: "Any Status" },
        ].map((s) => (
          <button key={s.v} data-testid={`task-status-${s.v}`} onClick={() => setStatusFilter(s.v)}
            className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold transition ${statusFilter === s.v ? "bg-[#0F172A] text-white" : "bg-white border border-[#E5E7EB] text-[#475569] hover:border-[#0F172A]"}`}>
            {s.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#475569]">Loading…</div>
      ) : tasks.length === 0 ? (
        <EmptyState icon={CheckSquare} title="No tasks here" hint="Add a task — maybe a payment follow-up in 10 days, or a sample delivery to a school."
          action={<Button onClick={() => openNew()}><Plus size={16}/> Add Task</Button>} />
      ) : (
        <div className="space-y-2" data-testid="tasks-list">
          {tasks.map((t) => {
            const pri = priorityMeta[t.priority] || priorityMeta.medium;
            const isOverdue = t.status === "pending" && t.due_date && t.due_date < todayIso();
            return (
              <div key={t.id} className="card-flat px-4 py-3 flex items-center gap-3" data-testid={`task-${t.id}`}>
                <button onClick={() => toggle(t)} data-testid={`toggle-${t.id}`}
                  className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center ${
                    t.status === "done" ? "bg-[#C1121F] border-[#C1121F]" : "border-[#94A3B8] hover:border-[#C1121F]"
                  }`}>
                  {t.status === "done" && <Check size={12} className="text-white" strokeWidth={3}/>}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold ${t.status === "done" ? "line-through text-[#94A3B8]" : "text-[#0F172A]"}`}>{t.title}</div>
                  {t.description && <div className="text-xs text-[#475569] mt-0.5">{t.description}</div>}
                  <div className="flex items-center gap-2 mt-1 text-xs">
                    <span className={`px-1.5 py-0.5 rounded font-semibold ${pri.cls}`}>{pri.label}</span>
                    {t.due_date && (
                      <span className={`flex items-center gap-1 ${isOverdue ? "text-red-600 font-semibold" : "text-[#475569]"}`}>
                        {isOverdue ? <AlertCircle size={11}/> : <Calendar size={11}/>}
                        {t.due_date}
                      </span>
                    )}
                    {t.assignee_name && t.assignee_name !== user?.name && (
                      <span className="text-[#94A3B8]">→ {t.assignee_name}</span>
                    )}
                  </div>
                </div>
                <button onClick={() => remove(t)} data-testid={`del-task-${t.id}`} className="text-[#94A3B8] hover:text-red-600 p-1"><Trash2 size={14}/></button>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Task">
        <form onSubmit={save} className="space-y-4">
          <Field label="Title" required>
            <input data-testid="task-title" required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="input-base" placeholder="e.g. Follow up payment from Apollo Hospital" />
          </Field>
          <Field label="Description">
            <textarea data-testid="task-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="input-base" rows={2} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Due Date">
              <input data-testid="task-due" type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} className="input-base" />
            </Field>
            <Field label="Priority">
              <select data-testid="task-priority" value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})} className="input-base">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </Field>
            {user?.role === "admin" && (
              <Field label="Assign To">
                <select data-testid="task-assignee" value={form.assigned_to} onChange={(e) => setForm({...form, assigned_to: e.target.value})} className="input-base">
                  <option value="">Self (me)</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </Field>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={() => { const d = new Date(); d.setDate(d.getDate() + 10); setForm({...form, due_date: d.toISOString().slice(0,10)}); }}>
              <Clock size={14}/> In 10 days
            </Button>
            <Button type="button" variant="secondary" onClick={() => { const d = new Date(); d.setDate(d.getDate() + 15); setForm({...form, due_date: d.toISOString().slice(0,10)}); }}>
              In 15 days
            </Button>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#E5E7EB]">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-task-btn">Add Task</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
